import { Group } from '@byecode/ui'
import { BACKGROUND_TYPE } from '@lighthouse/core'
import { ListItem4ByecodeUi } from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { InheritLabel } from '@/components/InheritLabel'

import { BackgroundField } from '../components/BackgroundField'
import { BorderField } from '../components/BorderField'
import { FillColor } from '../components/FillColor'
import { RadiusSize } from '../components/RadiusSize'
import { ShadowField } from '../components/ShadowField'
import { useSetExtendsKeys } from '../hooks/useMouseEventDistance'

export interface InteractionStyleSettingSwitch {
    enableColor?: boolean
    enableIconColor?: boolean
    enablePlaceholderColor?: boolean
    enableRadius?: boolean
    enableBackground?: boolean
    enableBorder?: boolean
    enableShadow?: boolean
    enableCheckedColor?: boolean
}

interface InteractionDropDownProps extends InteractionStyleSettingSwitch {
    title: string
    prefixName: string
    interactionType: 'hoverStyle' | 'activeStyle' | 'disabledStyle'
}

export const InteractionItem: React.FC<InteractionDropDownProps> = ({
    title,
    prefixName,
    interactionType,
    enableColor,
    enableIconColor,
    enablePlaceholderColor,
    enableRadius,
    enableBackground,
    enableBorder,
    enableShadow,
    enableCheckedColor
}) => {
    const { control } = useFormContext()
    const { handleSetBreakKey } = useSetExtendsKeys()
    return (
        <Controller
            control={control}
            name={`${prefixName}.${interactionType}.open`}
            render={({ field }) => {
                return (
                    <Group
                        label={<InheritLabel label={title} name={`${prefixName}.${interactionType}`} />}
                        mode="switch"
                        opened={!!field.value}
                        onCollapseChange={() => {
                            field.onChange(!field.value)
                            handleSetBreakKey(`${prefixName}.${interactionType}.open`)
                        }}
                    >
                        {enableColor && (
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <InheritLabel label="文本颜色" name={`${prefixName}.${interactionType}.design.color`} />
                                <Controller
                                    control={control}
                                    name={`${prefixName}.${interactionType}.design.color`}
                                    render={({ field }) => {
                                        return (
                                            <FillColor
                                                label="文本颜色"
                                                value={field.value}
                                                onChange={val => {
                                                    field.onChange(val)
                                                    handleSetBreakKey(`${prefixName}.${interactionType}.design.color`)
                                                }}
                                            />
                                        )
                                    }}
                                />
                            </ListItem4ByecodeUi>
                        )}

                        {enableIconColor && (
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <InheritLabel label="图标颜色" name={`${prefixName}.${interactionType}.design.iconColor`} />
                                <Controller
                                    control={control}
                                    name={`${prefixName}.${interactionType}.design.iconColor`}
                                    render={({ field }) => {
                                        return (
                                            <FillColor
                                                label="图标颜色"
                                                value={field.value}
                                                onChange={val => {
                                                    field.onChange(val)
                                                    handleSetBreakKey(`${prefixName}.${interactionType}.design.iconColor`)
                                                }}
                                            />
                                        )
                                    }}
                                />
                            </ListItem4ByecodeUi>
                        )}

                        {enablePlaceholderColor && (
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <InheritLabel label="占位符颜色" name={`${prefixName}.${interactionType}.design.placeholderColor`} />
                                <Controller
                                    control={control}
                                    name={`${prefixName}.${interactionType}.design.placeholderColor`}
                                    render={({ field }) => {
                                        return (
                                            <FillColor
                                                label="占位符颜色"
                                                value={field.value}
                                                onChange={val => {
                                                    field.onChange(val)
                                                    handleSetBreakKey(`${prefixName}.${interactionType}.design.placeholderColor`)
                                                }}
                                            />
                                        )
                                    }}
                                />
                            </ListItem4ByecodeUi>
                        )}
                        {enableRadius && (
                            <Controller
                                control={control}
                                name={`${prefixName}.${interactionType}.design.radius`}
                                render={({ field }) => (
                                    <RadiusSize
                                        title={<InheritLabel label="圆角" name={`${prefixName}.${interactionType}.design.radius`} />}
                                        value={field.value}
                                        onChange={val => {
                                            field.onChange(val)
                                            handleSetBreakKey(`${prefixName}.${interactionType}.design.radius`)
                                        }}
                                    />
                                )}
                            />
                        )}

                        {enableBackground && (
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <InheritLabel label="背景" name={`${prefixName}.${interactionType}.design.background`} />
                                <Controller
                                    control={control}
                                    name={`${prefixName}.${interactionType}.design.background`}
                                    render={({ field }) => (
                                        <BackgroundField
                                            value={field.value}
                                            onChange={val => {
                                                field.onChange(val)
                                                handleSetBreakKey(`${prefixName}.${interactionType}.design.background`)
                                            }}
                                        />
                                    )}
                                />
                            </ListItem4ByecodeUi>
                        )}
                        {enableBorder && (
                            <Controller
                                control={control}
                                name={`${prefixName}.${interactionType}.design.border`}
                                render={({ field }) => (
                                    <BorderField
                                        label={<InheritLabel label="描边" name={`${prefixName}.${interactionType}.design.border`} />}
                                        value={field.value}
                                        onChange={v => {
                                            if (isDeepEqual(v, field.value)) {
                                                return
                                            }
                                            field.onChange(v)
                                            handleSetBreakKey(`${prefixName}.${interactionType}.design.border`)
                                        }}
                                    />
                                )}
                            />
                        )}
                        {enableShadow && (
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <InheritLabel label="阴影" name={`${prefixName}.${interactionType}.design.shadow`} />
                                <Controller
                                    control={control}
                                    name={`${prefixName}.${interactionType}.design.shadow`}
                                    render={({ field }) => <ShadowField value={field.value} onChange={field.onChange} />}
                                />
                            </ListItem4ByecodeUi>
                        )}

                        {enableCheckedColor && (
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <InheritLabel label="选中后" name={`${prefixName}.${interactionType}.design.checkedColor`} />
                                <Controller
                                    control={control}
                                    name={`${prefixName}.${interactionType}.design.checkedColor`}
                                    render={({ field }) => (
                                        <FillColor
                                            label="选中后"
                                            value={field.value}
                                            enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                            onChange={val => {
                                                field.onChange(val)
                                                handleSetBreakKey(`${prefixName}.design.checkedColor`)
                                            }}
                                        />
                                    )}
                                />
                            </ListItem4ByecodeUi>
                        )}
                    </Group>
                )
            }}
        />
    )
}
